import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, Landmark, BookOpen } from "lucide-react";
import { springs } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { useAuditClauses } from "@/hooks/use-audit-clauses";
import type {
  AuditId,
  StandardClauseSet,
  StandardSection,
} from "@/packages/types";

// ─── Clause Colors ──────────────────────────────────────────────────────────

const CLAUSES_COLORS = {
  bar: "#E8862A",
  bg: "rgba(232,134,42,0.08)",
  text: "#E8862A",
};

// ─── Clause Row ─────────────────────────────────────────────────────────────

function ClauseRow({
  clauseRef,
  title,
  description,
}: {
  clauseRef: string;
  title: string;
  description?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <button
      type="button"
      onClick={() => setExpanded((prev) => !prev)}
      className="w-full text-left group/row"
    >
      <div
        className="rounded-[8px] border border-border bg-card transition-colors duration-150"
        style={{
          borderColor: expanded ? `${CLAUSES_COLORS.bar}40` : undefined,
          borderLeftWidth: 3,
          borderLeftColor: CLAUSES_COLORS.bar,
        }}
      >
        <div className="flex items-center gap-3 px-3.5 py-2.5">
          <span
            className="font-mono text-[11px] font-semibold tracking-tight shrink-0 min-w-[90px]"
            style={{ color: CLAUSES_COLORS.text }}
          >
            {clauseRef}
          </span>
          <span className="text-[13px] font-medium text-primary truncate flex-1">
            {title}
          </span>
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={springs.snappy}
            className="shrink-0"
          >
            <ChevronDown
              size={14}
              className="text-muted group-hover/row:text-secondary transition-colors"
            />
          </motion.span>
        </div>

        <AnimatePresence initial={false}>
          {expanded && description && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={springs.snappy}
              className="overflow-hidden"
            >
              <div className="px-3.5 pb-3 pt-0.5">
                <div className="h-px mb-2.5 bg-border" />
                <p className="text-[12.5px] text-secondary leading-relaxed">
                  {description}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </button>
  );
}

// ─── Section Group ──────────────────────────────────────────────────────────

function SectionGroup({
  section,
  defaultOpen,
}: {
  section: StandardSection;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 w-full group/section mb-2"
      >
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={springs.snappy}
        >
          <ChevronDown
            size={14}
            className="text-muted group-hover/section:text-secondary transition-colors"
          />
        </motion.span>
        <span className="text-[13px] font-semibold text-primary">
          {section.title}
        </span>
        <span className="text-[11px] text-muted ml-1">
          ({section.clauses.length})
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.snappy}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-1.5 pl-5 pb-4">
              {section.clauses.map((clause) => (
                <ClauseRow
                  key={clause.ref}
                  clauseRef={clause.ref}
                  title={clause.title}
                  description={clause.description}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Standard Header ────────────────────────────────────────────────────────

function StandardHeader({ clauseSet }: { clauseSet: StandardClauseSet }) {
  const totalClauses = clauseSet.sections.reduce(
    (sum, s) => sum + s.clauses.length,
    0,
  );

  return (
    <div className="rounded-[10px] border border-accent/20 bg-accent/[0.04] px-4 py-3.5">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-7 h-7 rounded-[6px] flex items-center justify-center bg-accent/10">
          <Landmark size={14} className="text-accent" />
        </div>
        <div>
          <p className="text-[14px] font-semibold text-primary leading-tight">
            {clauseSet.standard}
          </p>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: CLAUSES_COLORS.text }}
          >
            {clauseSet.abbreviation}
          </span>
        </div>
      </div>
      <p className="text-[12.5px] text-secondary leading-relaxed mb-2.5">
        {clauseSet.description}
      </p>
      <div className="flex items-center gap-3 text-[11px] text-muted">
        <span>
          {totalClauses} clause{totalClauses !== 1 ? "s" : ""}
        </span>
        <span className="text-border">|</span>
        <span>
          {clauseSet.sections.length} section
          {clauseSet.sections.length !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );
}

// ─── Empty State ────────────────────────────────────────────────────────────

function ClausesEmptyState() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <BookOpen size={48} className="text-muted" strokeWidth={1} />
        <p className="text-sm text-muted">
          No regulatory standard mapped to this audit
        </p>
      </div>
    </div>
  );
}

// ─── Main Tab Component ─────────────────────────────────────────────────────

interface AuditClausesTabProps {
  auditId: AuditId;
}

export function AuditClausesTab({ auditId }: AuditClausesTabProps) {
  const { t } = useTranslation(["audits"]);
  const { clauseSet, isLoading } = useAuditClauses(auditId);
  const [search, setSearch] = useState("");

  const filteredSections = useMemo(() => {
    if (!clauseSet) return [];
    if (!search.trim()) return clauseSet.sections;

    const q = search.toLowerCase();
    return clauseSet.sections
      .map((section) => ({
        ...section,
        clauses: section.clauses.filter(
          (c) =>
            c.ref.toLowerCase().includes(q) ||
            c.title.toLowerCase().includes(q),
        ),
      }))
      .filter((section) => section.clauses.length > 0);
  }, [clauseSet, search]);

  const totalFiltered = filteredSections.reduce(
    (sum, s) => sum + s.clauses.length,
    0,
  );

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <p className="text-xs text-muted">Loading clauses…</p>
        </div>
      </div>
    );
  }

  if (!clauseSet) return <ClausesEmptyState />;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-y-auto scrollbar-thin pr-1">
      <div className="space-y-4 pb-6">
        <StandardHeader clauseSet={clauseSet} />

        {/* Search */}
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
          />
          <input
            type="text"
            placeholder={t("audits:detail.clauses.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-[36px] pl-9 pr-3 rounded-[8px] bg-card border border-border text-[13px] text-primary placeholder:text-muted focus:outline-none focus:border-accent focus:shadow-[0_0_0_2px_rgba(232,134,42,0.12)] transition-all"
          />
        </div>

        {/* Summary strip */}
        {search.trim() && (
          <p className="text-[11px] text-muted">
            {totalFiltered} clause{totalFiltered !== 1 ? "s" : ""} matching "
            {search.trim()}"
          </p>
        )}

        {/* Sections */}
        {filteredSections.length > 0 ? (
          <div className="space-y-2">
            {filteredSections.map((section, idx) => (
              <SectionGroup
                key={section.title}
                section={section}
                defaultOpen={idx === 0}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-8">
            <Search size={32} className="text-muted" strokeWidth={1} />
            <p className="text-sm text-muted">No clauses match your search</p>
          </div>
        )}
      </div>
    </div>
  );
}
